import type { Plan } from "@/types/api";

interface PlanSelectionProps {
  plans: Plan[];
  selectedPlan: string;
  setSelectedPlan: (plan: string) => void;
}

export default function PlanSelection({
  plans,
  selectedPlan,
  setSelectedPlan,
}: PlanSelectionProps) {
  return (
    <div>
      <div className="text-center mb-10">
        <h2 className="font-serif text-3xl font-light text-[#2D2D2D] mb-4">
          요금제를 선택해주세요
        </h2>
        <p className="text-gray-500 text-sm font-light leading-relaxed">
          원하시는 스타일 개수에 맞는 요금제를 선택해주세요.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {plans.map((plan) => (
          <div
            key={plan.code}
            onClick={() => setSelectedPlan(plan.code)}
            className={`relative bg-white rounded-2xl p-8 border-2 cursor-pointer transition-all duration-300 ${
              selectedPlan === plan.code
                ? "border-[#C9A96E] shadow-xl scale-[1.02]"
                : "border-gray-200 hover:border-[#C9A96E] hover:shadow-lg"
            }`}
          >
            {plan.popular === "y" && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-[#C9A96E] text-white text-xs font-medium rounded-full whitespace-nowrap">
                인기
              </div>
            )}

            <div className="text-center mb-6">
              <h3 className="font-serif text-xl font-medium text-[#2D2D2D] mb-2">
                {plan.name}
              </h3>
              <div className="flex items-end justify-center gap-1">
                {plan.originalPrice > plan.price && (
                  <span className="text-sm text-gray-400 line-through mr-1">
                    {plan.originalPrice.toLocaleString()}
                  </span>
                )}
                <span className="text-3xl font-bold text-[#2D2D2D]">
                  {plan.price.toLocaleString()}
                </span>
                <span className="text-sm text-gray-500 mb-1">원</span>
              </div>
              {plan.discountRate > 0 && (
                <span className="text-xs text-red-500 font-medium">
                  {plan.discountRate}% 할인
                </span>
              )}
            </div>

            {/* Limits Badge */}
            <div className="flex items-center justify-center gap-2 mb-6 pb-6 border-b border-gray-100 flex-wrap">
              <div className="px-3 py-1.5 bg-[#FDF5EA] rounded-full">
                <span className="text-xs font-medium text-[#C9A96E]">
                  스타일 {plan.styleCount}개
                </span>
              </div>
              <div className="px-3 py-1.5 bg-[#FDF5EA] rounded-full">
                <span className="text-xs font-medium text-[#C9A96E]">
                  사진 {plan.photoCount}컷
                </span>
              </div>
              <div className="px-3 py-1.5 bg-[#FDF5EA] rounded-full">
                <span className="text-xs font-medium text-[#C9A96E]">
                  사진당 2회 재생성
                </span>
              </div>
              <div className="px-3 py-1.5 bg-[#FDF5EA] rounded-full">
                <span className="text-xs font-medium text-[#C9A96E]">
                  서비스 제공기간 3개월
                </span>
              </div>
            </div>

            <ul className="space-y-3 mb-6">
              {plan.features.map((feature, index) => (
                <li
                  key={index}
                  className="flex items-start gap-2 text-sm text-gray-600"
                >
                  <i className="ri-check-line text-[#C9A96E] mt-0.5 shrink-0"></i>
                  <span className="font-light">{feature}</span>
                </li>
              ))}
            </ul>

            {/* Description */}
            <div className="mt-6 pt-6 border-t border-gray-100">
              <p className="text-xs text-gray-500 font-light leading-relaxed text-center">
                {plan.description}
              </p>
            </div>

            {selectedPlan === plan.code && (
              <div className="absolute top-4 right-4 w-8 h-8 bg-[#C9A96E] rounded-full flex items-center justify-center">
                <i className="ri-check-line text-lg text-white"></i>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Info Box */}
      <div className="mt-8 bg-[#FDF5EA] rounded-xl p-6">
        <div className="flex items-start gap-3">
          <div className="w-6 h-6 flex items-center justify-center rounded-full bg-[#C9A96E] shrink-0 mt-0.5">
            <i className="ri-information-line text-sm text-white"></i>
          </div>
          <div>
            <p className="text-sm font-medium text-[#2D2D2D] mb-2">
              요금제 선택 가이드
            </p>
            <ul className="text-xs text-gray-600 font-light leading-relaxed space-y-1">
              {plans.map((p) => (
                <li key={p.code}>
                  • <strong>{p.name}:</strong> {p.description}
                </li>
              ))}
              <li className="pt-1 text-gray-500">
                • 서비스 제공기간(재생성 및 다운로드 가능 기한)은 결제일로부터 3개월입니다.
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
